import {PromoAction, Tag} from '../../../../services/cellcast.v2.types'

export interface Contact {
  id: string
  fullName: string
  source: string
  email: string
  phone: string
  cardName: string
  createdAt: string
  tags?: Tag[] | null
  noteVoice?: string | null
  promoSettings?: PromoAction[] | []
  countryCode?: string
  status?: string
  sales?: boolean
  replies?: number
  statusSubscribe?: string
  unsubscribedAt?: string
  outboundMessage?: string
  lastMessagePreview?: string
  lastMessagePreviewTime?: string
  flows?: Flow[]
  updatedAt?: string
  assignedTo?: {
    id: string
    name: string
  } | null

  interestedVehicleType?: string | null
  interestedVehicleTypes?: string[]

  // ===== NEW FIELDS FOR UPDATED COLUMNS =====

  // Optional fields used in mobile/card views
  cardOffers?: ({title?: string} | string)[] | null
  notesPreview?: string | null
  flow?: string | null

  // Card Offers (Selected card offers) - Use current promoSettings
  // cardOffers?: PromoAction[] | [] // Có thể dùng promoSettings thay thế
  lastVoice: {
    id: string
    leadIntent: string
    sentiment: string
    status: string
    voiceFile: string
    highlights: string[]
  } | null
  lastVoiceId: string
  // Voice Notes Summary (Summary from voice analysis)
  aiSummaryNote?: string | null
  notes?: string | null
  // Notes Preview (Preview of notes - can use current notes)
  // notesPreview?: string | null // Có thể dùng notes thay thế

  // Intent (Hot/Cold - customer's purchase intent status)
  intent?: string | null
  // Sentiment (Positive/Negative/Neutral - emotion from voice analysis)
  sentiment?: string | null
  // Last Reply Date (Last reply date)
  lastReplyDate?: string | null

  // New Bite/Message/Call timeline helpers
  // last reply time used to detect "new bite"
  lastReplyMessageTime?: string | null
  // last time we sent an outbound message
  outboundMessageTime?: string | null
  // last time we made a call to this contact
  callLastTime?: string | null

  // Last Reply (Content of the last reply)
  lastReply?: string | null
  // Notes (Notes)
  noteText?: string | null
  // Total Messages Sent (Total number of messages sent)
  totalMessagesSent?: number

  // Last Action Type (Message/Call - type of the last action)
  lastActionType?: 'message' | 'call' | 'email' | string

  // Last Action Date (Time of the last action - can use updatedAt)
  lastActionDate?: string

  // Last Outbound Message Date (Date of the last outbound message)
  lastOutboundMessageDate?: string | null

  // Days Since Last Outbound Message (Number of days since the last outbound message)
  daysSinceLastOutbound?: number

  // Days Since Last Reply (Number of days since the last reply)
  daysSinceLastReply?: number

  // Status Ai Summary Note (Status of the ai summary note)
  statusAiSummaryNote?: string | null

  // Welcome Message (Welcome message)
  welcomeMessage?: string | null

  // Last Message Preview (Last message preview)
  countSentMessages?: number

  // Lead Status (Lead status)
  leadStatus?: string | null
  value?: number | null
}

export enum STATUS_CONTACT {
  UNVERIFIED = 'unverified',
  VERIFIED = 'verified',
  PAUSED = 'paused',
}

// ===== NEW ENUMS FOR ADDITIONAL FIELDS =====

// Intent levels for lead qualification
export enum INTENT_LEVEL {
  HOT = 'hot',           // High purchase intent
  WARM = 'warm',         // Interested but undecided
  COLD = 'cold',         // Low interest
}

// Sentiment analysis results
export enum SENTIMENT_TYPE {
  POSITIVE = 'positive', // Positive sentiment
  NEGATIVE = 'negative', // Negative sentiment
  NEUTRAL = 'neutral',   // Neutral sentiment
}

// Last action types
export enum LAST_ACTION_TYPE {
  MESSAGE = 'message',   // Message
  CALL = 'call',        // Call
  EMAIL = 'email',      // Email
  VOICE_NOTE = 'voice_note', // Voice note
}
export interface Flow {
  id: string
  name: string
  status?: string
}

// ===== FILTER TYPES AND DATA =====
// Centralized filter values and mappings used by SearchBar and data fetching
export type ContactFilter =
  | 'all'
  | 'lead-today'
  | 'lead-weekly'
  | 'lead-monthly'
  | 'bite-today'
  | 'bite-weekly'
  | 'bite-monthly'
  | 'sale-today'
  | 'sale-weekly'
  | 'sale-monthly'

export const CONTACT_FILTER_OPTIONS: { value: ContactFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.ALL' },
  { value: 'lead-today', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.LEAD_TODAY' },
  { value: 'lead-weekly', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.LEAD_WEEKLY' },
  { value: 'lead-monthly', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.LEAD_MONTHLY' },
  { value: 'bite-today', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.BITE_TODAY' },
  { value: 'bite-weekly', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.BITE_WEEKLY' },
  { value: 'bite-monthly', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.BITE_MONTHLY' },
  { value: 'sale-today', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.SALE_TODAY' },
  { value: 'sale-weekly', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.SALE_WEEKLY' },
  { value: 'sale-monthly', labelKey: 'RAW_CONTACT_COLLECTION.FILTER.SALE_MONTHLY' },
]

export const CONTACT_FILTER_QUERY_MAP: Record<
  ContactFilter,
  { dateType?: 'day' | 'week' | 'month'; lastReplyMessageExists?: boolean; sales?: boolean }
> = {
  all: {},
  'lead-today': { dateType: 'day' },
  'lead-weekly': { dateType: 'week' },
  'lead-monthly': { dateType: 'month' },
  'bite-today': { lastReplyMessageExists: true, dateType: 'day' },
  'bite-weekly': { lastReplyMessageExists: true, dateType: 'week' },
  'bite-monthly': { lastReplyMessageExists: true, dateType: 'month' },
  'sale-today': { sales: true, dateType: 'day' },
  'sale-weekly': { sales: true, dateType: 'week' },
  'sale-monthly': { sales: true, dateType: 'month' },
}

export type GetContactListResponse = {
  data?: {
    data?: {
      items: Contact[]
      total?: number
      totalLeads?: number
      totalLeadGrowth?: number
      totalPages?: number
      totalWarmUp?: number
      totalWarmUpGrowth?: number
      countValid?: number
    }
  }
}

export type AddContactResponse = {
  items?: Contact[]
  message?: string
  status?: boolean
}

export interface VoiceAnalysisResponse {
  items: VoiceItem[]
  total: number
  limit: number
  totalPages: number
  current: number
  pagingCounter: number
  hasPrevPage: boolean
  hasNextPage: boolean
  prevPage: number | null
  nextPage: number | null
}

export interface VoiceItem {
  id: string
  _id: string
  transcript: string
  voiceFile: string
  summary: string
  highlights: string[]
  actionItems: string[]
  sentiment: string
  leadIntent: string
  status: 'completed' | 'pending' | 'failed'
  rawContact: RawContact
  createdAt: string // ISO 8601 format
  updatedAt: string // ISO 8601 format
  welcomeMessage: string
  nextCallOpeners: string[]
  suggestedNextSteps: string[]
}

export interface RawContact {
  id: string
  _id: string
  fullName: string
  phone: string
  email: string | null
}
